import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Button, Card, Col, Container, Row, Spinner } from "reactstrap";
import dayjs from "dayjs";

import {
  getDashboardOperadorSalida,
  postDashboardOperadorBuscarTiempos,
} from "../../../helpers/backend_helper";
import { Alert, ConfirmAlertHelper, ToastHelper } from "../../../helpers/alerts/alertHelper";
import { ESTADOS, getDesviacion, getEstado, hhmm } from "../SalidasStatus/statusHelper";
import { ESTADOS_SALIDA } from "./operadorHelper";
import BitacoraTable from "./BitacoraTable";
import AccionesSalida from "./AccionesSalida";
import "../SalidasStatus/SalidasStatus.css";
import "./OperadorDashboard.css";

const Dato = ({ label, value }) => (
  <div>
    <div className="operador-overline" style={{ color: "#777a89" }}>{label}</div>
    <div style={{ fontSize: "14px", fontWeight: 600 }}>{value ?? "—"}</div>
  </div>
);

const fechaHora = (valor) => (valor ? dayjs(valor).format("DD/MM/YYYY HH:mm") : "—");
const num = (valor) => (valor == null ? 0 : valor);
// pasajeros y pasajeros_ninos son decimal(12,2) en la BD: llegan como "38.00"
const entero = (valor) => (valor == null ? 0 : Math.round(Number(valor)));

/*
  Pantalla de detalle de una salida del operador. A diferencia del modal que sustituye,
  tiene URL propia, así que sobrevive a un F5 y se puede compartir; por eso carga sus
  datos del endpoint en vez de recibirlos de la lista.
*/
const SalidaDetalle = () => {
  document.title = "Detalle de salida | Sistema de Logística";

  const { id } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [buscando, setBuscando] = useState(false);
  const [salida, setSalida] = useState(null);
  const [error, setError] = useState(null);

  const getSalida = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDashboardOperadorSalida(id, { withToast: false });
      if (response?.status && response.salida) {
        setSalida(response.salida);
        setError(null);
      } else {
        setError(response?.message || "No se pudo cargar la salida.");
      }
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar la salida.");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    getSalida();
  }, [getSalida]);

  // Única acción que modifica datos: vuelve a correr la conciliación GPS de esta salida
  const buscarTiempos = () => {
    ConfirmAlertHelper(
      async (confirmed) => {
        if (!confirmed) return;
        setBuscando(true);
        try {
          const response = await postDashboardOperadorBuscarTiempos(id, {});
          if (response?.status && response.salida) {
            setSalida(response.salida);
          }
        } catch (e) {
          console.error(e);
          Alert("No se pudieron consultar los tiempos.", false);
        }
        setBuscando(false);
      },
      "Buscar tiempos",
      "Se volverán a consultar los cruces de geocerca de esta salida y se recalcularán sus tiempos. ¿Continuar?",
      "Buscar tiempos"
    );
  };

  const proximamente = (accion) => ToastHelper(`${accion}: disponible pronto`);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (!salida) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card className="salidas-status-card operador-card">
            <div style={{ padding: "32px 22px", textAlign: "center" }}>
              <div className="text-muted" style={{ fontSize: "13px" }}>
                {error ?? "Esta salida no está disponible."}
              </div>
              <Link to="/dashboard" className="btn btn-light btn-sm mt-3">
                <i className="mdi mdi-arrow-left me-1"></i> Volver a mis salidas
              </Link>
            </div>
          </Card>
        </Container>
      </div>
    );
  }

  const ciclo = ESTADOS_SALIDA[salida.estado] ?? ESTADOS_SALIDA.programada;
  const desviacion = getDesviacion(salida);
  const puntualidad = ESTADOS[getEstado(desviacion.desv, desviacion.tolerancia)];
  const generales = salida.generales ?? {};
  const boletos = generales.boletos ?? {};

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <Card className="salidas-status-card operador-card">
                <div className="salidas-status-header">
                  <Button color="light" size="sm" onClick={() => history.push("/dashboard")}>
                    <i className="mdi mdi-arrow-left me-1"></i> Mis salidas
                  </Button>
                  <div>
                    <div style={{ fontSize: "17px", fontWeight: 700, lineHeight: 1.2 }}>
                      Salida #{salida.id} · {salida.ramal_nombre}
                    </div>
                    <div style={{ fontSize: "11.5px", color: "#777a89", marginTop: 2 }}>
                      Tiempo {salida.timer ?? "-"} · {salida.tipo_label} · Unidad {salida.unidad ?? "-"}
                    </div>
                  </div>
                  <div style={{ flex: 1 }} />
                  <Button color="light" size="sm" onClick={getSalida}>
                    <i className="mdi mdi-refresh me-1"></i> Actualizar
                  </Button>
                </div>

                <div style={{ padding: "16px 22px 22px" }}>
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                    <span className="operador-pill" style={{ background: ciclo.bg, border: `1px solid ${ciclo.bd}`, color: ciclo.ink }}>
                      {ciclo.label}
                    </span>
                    <span className="operador-pill" style={{ background: puntualidad.bg, border: `1px solid ${puntualidad.bd}`, color: puntualidad.ink }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: puntualidad.color }} />
                      {puntualidad.label}
                    </span>
                    {salida.min_quemados > 0 && (
                      <span style={{ fontSize: "12px", color: ESTADOS.que.ink, fontWeight: 600 }}>
                        {salida.min_quemados}′ quemados · {salida.quemadas} checador(es)
                      </span>
                    )}
                  </div>

                  <div className="d-flex flex-wrap" style={{ gap: 10, margin: "14px 0 18px" }}>
                    <div className="operador-tile-info" style={{ flex: "1 1 140px" }}>
                      <div className="operador-overline" style={{ color: "#777a89" }}>Salida</div>
                      <div style={{ fontSize: "19px", fontWeight: 700 }}>{salida.hora}</div>
                      <div style={{ fontSize: "11.5px", color: "#777a89" }}>Real {hhmm(salida.salida_real)}</div>
                    </div>
                    <div className="operador-tile-info" style={{ flex: "1 1 140px" }}>
                      <div className="operador-overline" style={{ color: "#777a89" }}>Regreso</div>
                      <div style={{ fontSize: "19px", fontWeight: 700 }}>{hhmm(salida.regreso)}</div>
                      <div style={{ fontSize: "11.5px", color: "#777a89" }}>
                        {salida.regreso ? "Registrado" : "Pendiente"}
                      </div>
                    </div>
                  </div>

                  <AccionesSalida
                    salida={salida}
                    buscando={buscando}
                    onBuscarTiempos={buscarTiempos}
                    onBoletera={() => history.push(`/dashboard-operador/salida/${salida.id}/boletera`)}
                    onProximamente={proximamente}
                  />

                  <div style={{ fontSize: "15px", fontWeight: 600, margin: "22px 0 12px" }}>Generales</div>
                  <div className="operador-generales" style={{ marginBottom: 24 }}>
                    <Dato label="Fecha / hora salida" value={fechaHora(salida.date)} />
                    <Dato label="Fecha / hora regreso" value={fechaHora(salida.regreso)} />
                    <Dato label="Unidad" value={salida.unidad} />
                    <Dato label="Destino" value={salida.destination} />
                    <Dato label="Operador" value={generales.operator_name} />
                    <Dato label="Analista" value={generales.analista} />
                    <Dato label="Tipo de salida" value={salida.tipo_label} />
                    {/* pagoAdministrativo no guarda salida_id, asi que el pago no es atribuible a esta salida */}
                    <Dato label="Pago administrativo" value="—" />
                    {/* El total es la cifra del motor (la que suman reportes y nómina), no la suma del desglose */}
                    <Dato
                      label="Pasajeros (sub/baj/blq)"
                      value={`${entero(generales.pasajeros)} (${num(generales.pasaje_subida)}/${num(generales.pasaje_bajada)}/${num(generales.pasaje_bloqueos)})`}
                    />
                    <Dato
                      label="Niños (sub/baj)"
                      value={`${entero(generales.pasajeros_ninos)} (${num(generales.pasaje_subida_ninos)}/${num(generales.pasaje_bajada_ninos)})`}
                    />
                    <Dato label="Boletos adulto" value={boletos.adulto_total} />
                    <Dato label="Boletos menor" value={boletos.menor_total} />
                    <Dato label="Boletos totales" value={boletos.total} />
                  </div>

                  <BitacoraTable
                    checkpoints={salida.time_geofences}
                    resumen={salida.bitacora_resumen}
                    remoteData={salida.remote_data}
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SalidaDetalle;
